export default defineNuxtRouteMiddleware(async (to) => {
    if (import.meta.server) {
        return;
    }

    const authStore = useAuthStore();
    const portalStore = useClientPortalStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();
    const { getHostname, isCustomDomain, resolveCustomDomainTenantId } = useDomainResolver();
    const auth = useNuxtApp().$auth;
    const accessUnavailableError = () => createError({
        statusCode: 503,
        statusMessage: 'Account access could not be loaded. Please refresh and try again.',
    });
    const queryTenantHint = typeof to.query.tenantId === 'string' ? to.query.tenantId : null;
    let tenantHint = queryTenantHint;

    const hostname = getHostname();
    if (isCustomDomain(hostname)) {
        const resolved = await resolveCustomDomainTenantId(hostname);
        tenantHint = resolved || tenantHint;
    }

    await authStore.awaitAuthReady();
    const user = authStore.user || auth.currentUser;

    if (!user) {
        const query: Record<string, string> = {
            redirect: to.fullPath || '/portal',
        };

        if (tenantHint) {
            query.tenantId = tenantHint;
        }

        return navigateTo({
            path: '/auth/client-login',
            query,
        });
    }

    if (!userStore.user || userStore.user.id !== user.uid) {
        const profileResolved = await userStore.fetchUser(user.uid);
        if (!profileResolved) {
            throw accessUnavailableError();
        }
    }

    const clientSlugParam = to.params.tenantSlug;
    if (typeof clientSlugParam !== 'string' || clientSlugParam.length === 0) {
        return navigateTo('/portal');
    }

    const clientSlug = clientSlugParam.trim().toLowerCase();

    if (tenantStore.loadedForUserId !== user.uid && userStore.hasTeamAccess) {
        const membershipsResolved = await tenantStore.fetchUserTenants(user.uid);
        if (!membershipsResolved) {
            throw accessUnavailableError();
        }
    }

    const previewClientId = typeof to.query.asClientId === 'string' ? to.query.asClientId : null;

    if (previewClientId) {
        const tenantMembership = tenantHint
            ? tenantStore.tenants.find((entry) => entry.tenantId === tenantHint)
            : null;
        const isAdminPreview = tenantMembership && ['owner', 'admin'].includes(tenantMembership.role);
        if (!isAdminPreview) {
            return navigateTo('/portal');
        }
    }

    const hasAccess = await portalStore.initializeForClientSlug(clientSlug, {
        previewClientId,
        tenantHint,
    });
    if (!hasAccess) {
        if (userStore.hasTeamAccess) {
            const firstTenantId = tenantStore.currentTenantId || tenantStore.tenants[0]?.tenantId;
            if (firstTenantId) {
                return navigateTo(`/${firstTenantId}/dashboard`);
            }
        }
        return navigateTo('/portal');
    }

    const uid = user.uid;
    if (uid && typeof window !== 'undefined') {
        localStorage.setItem(`portal:lastClientSlug:${uid}`, clientSlug);
    }
});
